import {Injectable} from '@angular/core';

export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeInMB?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageCompressionService {

  constructor() {
  }

  /**
   * Compress an image file to reduce its size
   * @param file - The image file to compress
   * @param options - Compression options
   * @returns Promise<File> - The compressed image file
   */
  async compressImage(file: File, options: ImageCompressionOptions = {}): Promise<File> {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.8,
      maxSizeInMB = 5
    } = options;

    // If the file is already smaller than the max size, return it as-is
    if (file.size <= maxSizeInMB * 1024 * 1024) {
      return file;
    }

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        const {width, height} = this.calculateNewDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress the image
        ctx!.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob with compression
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new File from the compressed blob
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });

              // If the compressed file is still too large, try with lower quality
              if (compressedFile.size > maxSizeInMB * 1024 * 1024 && quality > 0.3) {
                // Recursively compress with lower quality
                this.compressImage(file, {...options, quality: quality - 0.1})
                  .then(resolve)
                  .catch(reject);
              } else {
                resolve(compressedFile);
              }
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // Load the image
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Compress multiple images concurrently
   * @param files - Array of image files to compress
   * @param options - Compression options
   * @returns Promise<File[]> - Array of compressed image files
   */
  async compressImages(files: File[], options: ImageCompressionOptions = {}): Promise<File[]> {
    const compressionPromises = files.map(file => this.compressImage(file, options));
    return Promise.all(compressionPromises);
  }

  /**
   * Check if a file is an image
   * @param file - The file to check
   * @returns boolean - True if the file is an image
   */
  isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Get the estimated compression ratio for display purposes
   * @param originalSize - Original file size in bytes
   * @param compressedSize - Compressed file size in bytes
   * @returns string - Formatted compression ratio
   */
  getCompressionRatio(originalSize: number, compressedSize: number): string {
    const ratio = ((originalSize - compressedSize) / originalSize) * 100;
    return `${ratio.toFixed(1)}%`;
  }

  /**
   * Format file size for display
   * @param bytes - File size in bytes
   * @returns string - Formatted file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Calculate new dimensions while maintaining aspect ratio
   * @param originalWidth - Original image width
   * @param originalHeight - Original image height
   * @param maxWidth - Maximum allowed width
   * @param maxHeight - Maximum allowed height
   * @returns Object with new width and height
   */
  private calculateNewDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let width = originalWidth;
    let height = originalHeight;

    // Calculate the scaling factor
    const widthRatio = maxWidth / width;
    const heightRatio = maxHeight / height;
    const ratio = Math.min(widthRatio, heightRatio);

    // Only scale down, never scale up
    if (ratio < 1) {
      width *= ratio;
      height *= ratio;
    }

    return {
      width: Math.round(width),
      height: Math.round(height)
    };
  }
} 
